import { useEffect, useRef, useState } from "react";
import type { Api } from "../lib/api";
import type { MentionCandidate } from "../lib/types";

/**
 * Detect an in-progress `@token` immediately before the caret. The `@` must sit at
 * the very start of the text or follow whitespace, and there must be no whitespace
 * between it and the caret. Returns the `@`'s index and the typed token (may be "").
 */
export function activeMentionQuery(
  value: string,
  caret: number,
): { start: number; query: string } | null {
  for (let i = caret - 1; i >= 0; i--) {
    const ch = value[i]!;
    if (ch === "@") {
      const prev = i > 0 ? value[i - 1]! : " ";
      if (i === 0 || /\s/.test(prev)) return { start: i, query: value.slice(i + 1, caret) };
      return null;
    }
    if (/\s/.test(ch)) return null;
  }
  return null;
}

/**
 * A textarea with an inline `@firstname` mention typeahead. As the user types `@`
 * followed by a name fragment, it fetches matching candidates (same-account
 * teammates + PumaMesh admins) and shows a dropdown; picking one inserts `@email `.
 * The candidate set is scoped server-side, so this never reveals other accounts.
 */
export function MentionTextarea({
  api,
  value,
  onChange,
  className = "input",
  placeholder,
  disabled,
  ticketId,
}: {
  api: Api;
  value: string;
  onChange: (next: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  /** When set, the picker also offers the customer users on this ticket's account. */
  ticketId?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [candidates, setCandidates] = useState<MentionCandidate[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [start, setStart] = useState(-1);
  const [query, setQuery] = useState("");

  function sync(el: HTMLTextAreaElement) {
    const m = activeMentionQuery(el.value, el.selectionStart ?? el.value.length);
    if (m) {
      setStart(m.start);
      setQuery(m.query);
    } else {
      setStart(-1);
      setOpen(false);
    }
  }

  // Debounced candidate fetch whenever an @query is active.
  useEffect(() => {
    if (start < 0) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.mentionCandidates(query, ticketId);
        if (cancelled) return;
        setCandidates(res.candidates);
        setActive(0);
        setOpen(res.candidates.length > 0);
      } catch {
        if (!cancelled) {
          setCandidates([]);
          setOpen(false);
        }
      }
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [api, start, query, ticketId]);

  function pick(c: MentionCandidate) {
    const el = ref.current;
    const caret = el?.selectionStart ?? value.length;
    if (start < 0) return;
    const before = value.slice(0, start);
    const after = value.slice(caret);
    const insertEnd = `${before}@${c.email} `.length;
    onChange(`${before}@${c.email} ${after}`);
    setOpen(false);
    setStart(-1);
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(insertEnd, insertEnd);
      }
    });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!open || candidates.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (a + 1) % candidates.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a - 1 + candidates.length) % candidates.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      const c = candidates[active];
      if (c) {
        e.preventDefault();
        pick(c);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div className="relative">
      <textarea
        ref={ref}
        className={className}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.value);
          sync(e.target);
        }}
        onKeyUp={(e) => sync(e.currentTarget)}
        onClick={(e) => sync(e.currentTarget)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
      />
      {open && (
        <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-auto rounded-sm border border-line bg-bg shadow-lg">
          {candidates.map((c, i) => (
            <li key={c.email}>
              <button
                type="button"
                className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ${
                  i === active ? "bg-accent/10 text-ink" : "text-ink-muted"
                }`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(c);
                }}
              >
                <span className="min-w-0">
                  <span className="block truncate text-ink">{c.name}</span>
                  <span className="block truncate text-xs text-ink-dim">{c.email}</span>
                </span>
                <span className={c.kind === "admin" ? "badge-accent" : "badge"}>
                  {c.kind === "admin" ? "PumaMesh" : "Teammate"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
